import React, { useState, useEffect } from 'react';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import usersService, { User, Role } from '../services/usersService';
import './UserManagement.css';


const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<Partial<User> | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(10);

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const users = await usersService.getAllUsers();
      // Sort users by name in ascending order
      const sortedUsers = users.sort((a, b) => 
        a.employeeName.localeCompare(b.employeeName)
      );
      setUsers(sortedUsers);
      setError(null);
    } catch (err) {
      setError('Failed to fetch users');
      console.error('Error fetching users:', err);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const rolesData = await usersService.getAllRoles();
      setRoles(rolesData);
    } catch (err) {
      console.error('Error fetching roles:', err);
      // Fallback to default roles if API fails
      setRoles([
        { id: 1, name: 'Employee', description: 'Default employee role' },
        { id: 2, name: 'HR Admin', description: 'HR management role' },
        { id: 3, name: 'PMO Admin', description: 'PMO management role' },
        { id: 4, name: 'Super Admin', description: 'Full system access' }
      ]);
    }
  };

  // Helper function to format date for input[type="date"]
  const formatDateForInput = (dateString: string | undefined): string => {
    if (!dateString) return '';
    // If date is in ISO format (2026-06-04T00:00:00.000Z), extract just the date part
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleAddClick = () => {
    setEditingUser({
      employeeName: '',
      email: '',
      designation: '',
      department: '',
      building: '',
      floor: '',
      extNo: '',
      contactNo: '',
      employeeCode: '',
      location: '',
      dateOfJoining: '',
      dateOfBirth: '',
      weddingAnniversary: '',
      roleId: 1,
      isActive: true
    });
    setIsModalOpen(true);
  };

  const handleEditClick = (user: User) => {
    setEditingUser({
      id: user.id,
      employeeName: user.employeeName,
      email: user.email,
      designation: user.designation || '',
      department: user.department || '',
      building: user.building || '',
      floor: user.floor || '',
      extNo: user.extNo || '',
      contactNo: user.contactNo || '',
      employeeCode: user.employeeCode || '',
      location: user.location || '',
      dateOfJoining: formatDateForInput(user.dateOfJoining),
      dateOfBirth: formatDateForInput(user.dateOfBirth),
      weddingAnniversary: formatDateForInput(user.weddingAnniversary),
      roleId: user.roleId,
      isActive: user.isActive
    });
    setIsModalOpen(true);
  };

  const handleDeleteClick = (user: User) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const handleToggleStatus = async (userId: number) => {
    try {
      await usersService.toggleUserStatus(userId);
      setSaveMessage('User status updated successfully');
      setTimeout(() => setSaveMessage(''), 3000);
      fetchUsers();
    } catch (err: any) {
      setError(err.message || 'Failed to update user status');
      console.error('Error updating user status:', err);
    }
  };

  const handleSave = async () => {
    if (!editingUser) return;

    try {
      const userData = {
        employeeName: editingUser.employeeName!,
        email: editingUser.email!,
        designation: editingUser.designation,
        department: editingUser.department,
        building: editingUser.building,
        floor: editingUser.floor,
        extNo: editingUser.extNo,
        contactNo: editingUser.contactNo,
        employeeCode: editingUser.employeeCode,
        location: editingUser.location,
        dateOfJoining: editingUser.dateOfJoining,
        dateOfBirth: editingUser.dateOfBirth,
        weddingAnniversary: editingUser.weddingAnniversary,
        roleId: editingUser.roleId
      };

      if (editingUser.id) {
        await usersService.updateUser(editingUser.id, userData);
        setSaveMessage('User updated successfully');
      } else {
        await usersService.createUser(userData);
        setSaveMessage('User added successfully');
      }

      setTimeout(() => setSaveMessage(''), 3000);
      setIsModalOpen(false);
      setEditingUser(null);
      setCurrentPage(1); // Reset to first page
      fetchUsers();
    } catch (err: any) {
      setError(err.message || 'Failed to save user');
      console.error('Error saving user:', err);
    }
  };

  const handleDelete = async () => {
    if (!userToDelete) return;

    try {
      await usersService.deleteUser(userToDelete.id);
      setSaveMessage('User deleted successfully');
      setTimeout(() => setSaveMessage(''), 3000);
      setShowDeleteModal(false);
      setUserToDelete(null);
      setCurrentPage(1); // Reset to first page
      fetchUsers();
    } catch (err: any) {
      setError(err.message || 'Failed to delete user');
      console.error('Error deleting user:', err);
    }
  };

  const handleBulkUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setError(null);
    setUploadResults(null);

    try {
      // Dynamically import xlsx library
      const XLSX = await import('xlsx');
      
      // Read the Excel file
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      
      // Get the first sheet
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      
      // Convert to JSON with headers as keys
      const jsonData = XLSX.utils.sheet_to_json(worksheet);
      
      console.log('Total rows in Excel:', jsonData.length);
      if (jsonData.length > 0) {
        console.log('First row sample:', jsonData[0]);
        console.log('Headers detected:', Object.keys(jsonData[0] as any));
      }

      if (jsonData.length === 0) {
        setError('Excel file is empty or has no data rows');
        setUploading(false);
        return;
      }

      // Helper function to parse Excel date
      const parseExcelDate = (value: any): string | undefined => {
        if (!value) return undefined;
        
        // If it's already a string in date format
        if (typeof value === 'string') {
          const date = new Date(value);
          if (!isNaN(date.getTime())) {
            // Use UTC to avoid timezone offset issues
            const year = date.getUTCFullYear();
            const month = String(date.getUTCMonth() + 1).padStart(2, '0');
            const day = String(date.getUTCDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
          }
        }
        
        // If it's an Excel serial number (days since 1900-01-01)
        if (typeof value === 'number') {
          // Excel dates are days since 1900-01-01 (with a leap year bug at 1900)
          // Subtract 1 for Excel's 1900 leap year bug, and 1 more for 0-indexing
          const daysOffset = value - 2;
          const millisecondsPerDay = 24 * 60 * 60 * 1000;
          const excelEpoch = Date.UTC(1900, 0, 1);
          const dateMs = excelEpoch + (daysOffset * millisecondsPerDay);
          const date = new Date(dateMs);
          
          if (!isNaN(date.getTime())) {
            // Use UTC to avoid timezone offset issues
            const year = date.getUTCFullYear();
            const month = String(date.getUTCMonth() + 1).padStart(2, '0');
            const day = String(date.getUTCDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
          }
        }
        
        return undefined;
      };

      // Helper function to get value from row by checking multiple possible header names
      const getValue = (row: any, possibleNames: string[]): any => {
        for (const name of possibleNames) {
          // Check exact match (case-insensitive)
          const key = Object.keys(row).find(k => k.toLowerCase().trim() === name.toLowerCase());
          if (key && row[key]) return row[key];
        }
        return undefined;
      };
      
      let filteredCount = 0;
      const users = (jsonData as any[]).map((row: any, index: number) => {
        const user = {
          employeeName: getValue(row, ['employee_name', 'employeeName', 'name', 'Employee Name', 'Name']) 
            ? String(getValue(row, ['employee_name', 'employeeName', 'name', 'Employee Name', 'Name'])).trim() : '',
          designation: getValue(row, ['designation', 'Designation', 'title', 'Title']) 
            ? String(getValue(row, ['designation', 'Designation', 'title', 'Title'])).trim() : '',
          department: getValue(row, ['department', 'Department', 'dept', 'Dept']) 
            ? String(getValue(row, ['department', 'Department', 'dept', 'Dept'])).trim() : '',
          email: getValue(row, ['email', 'Email', 'email_id', 'Email ID']) 
            ? String(getValue(row, ['email', 'Email', 'email_id', 'Email ID'])).trim() : '',
          building: getValue(row, ['building', 'Building']) 
            ? String(getValue(row, ['building', 'Building'])).trim() : '',
          floor: getValue(row, ['floor', 'Floor']) 
            ? String(getValue(row, ['floor', 'Floor'])).trim() : '',
          extNo: getValue(row, ['ext_no', 'extNo', 'extension', 'Extension', 'ext no', 'Ext No']) 
            ? String(getValue(row, ['ext_no', 'extNo', 'extension', 'Extension', 'ext no', 'Ext No'])).trim() : '',
          contactNo: getValue(row, ['contact_no', 'contactNo', 'phone', 'Phone', 'mobile', 'Mobile', 'contact no', 'Contact No']) 
            ? String(getValue(row, ['contact_no', 'contactNo', 'phone', 'Phone', 'mobile', 'Mobile', 'contact no', 'Contact No'])).trim() : '',
          employeeCode: getValue(row, ['Employee Code', 'employee_code', 'employeeCode', 'emp_code', 'Emp Code', 'code', 'Code']) 
            ? String(getValue(row, ['Employee Code', 'employee_code', 'employeeCode', 'emp_code', 'Emp Code', 'code', 'Code'])).trim() : '',
          location: getValue(row, ['Location', 'location', 'office', 'Office', 'site', 'Site']) 
            ? String(getValue(row, ['Location', 'location', 'office', 'Office', 'site', 'Site'])).trim() : '',
          dateOfJoining: parseExcelDate(getValue(row, ['Date of Joining', 'date_of_joining', 'dateOfJoining', 'joining_date', 'Joining Date', 'DOJ'])),
          dateOfBirth: parseExcelDate(getValue(row, ['Date of Birth', 'date_of_birth', 'dateOfBirth', 'birth_date', 'Birth Date', 'DOB'])),
          weddingAnniversary: parseExcelDate(getValue(row, ['Wedding anniversary', 'wedding_anniversary', 'weddingAnniversary', 'anniversary', 'Anniversary']))
        };
        
        if (index < 3) {
          console.log(`Row ${index + 1}:`, user);
        }
        return user;
      }).filter((user: any, index: number) => {
        const isValid = user.email && user.employeeName;
        if (!isValid) {
          filteredCount++;
          if (filteredCount <= 5) {
            console.log(`Filtered out row ${index + 1}:`, user, 'Reason:', !user.email ? 'No email' : 'No name');
          }
        }
        return isValid;
      });
      
      if (filteredCount > 0) {
        console.log(`Total filtered out: ${filteredCount} rows`);
      }

      console.log('Valid users to upload:', users.length);
      console.log('First 3 users:', users.slice(0, 3));
      console.log('Last 3 users:', users.slice(-3));

      if (users.length === 0) {
        setError('No valid users found in Excel file');
        setUploading(false);
        return;
      }

      console.log('Sending to backend...');
      const result = await usersService.bulkUploadUsers(users);
      console.log('Backend response:', result);
      setUploadResults(result.results);
      
      let message = `Bulk upload completed: ${result.results.created} created, ${result.results.updated} updated, ${result.results.skipped} skipped`;
      if (result.results.errors && result.results.errors.length > 0) {
        message += `\n\nErrors (${result.results.errors.length}):\n${result.results.errors.slice(0, 5).join('\n')}`;
        if (result.results.errors.length > 5) {
          message += `\n... and ${result.results.errors.length - 5} more errors`;
        }
      }
      
      setSaveMessage(message);
      setTimeout(() => {
        setSaveMessage('');
        setUploadResults(null);
      }, 15000);
      setCurrentPage(1); // Reset to first page
      fetchUsers();
    } catch (err: any) {
      setError(err.message || 'Failed to upload Excel file');
      console.error('Error uploading Excel file:', err);
    } finally {
      setUploading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  // Filter users based on search query
  const filteredUsers = users.filter(user => {
    const query = searchQuery.toLowerCase();
    return (
      user.employeeName.toLowerCase().includes(query) ||
      user.email.toLowerCase().includes(query) ||
      (user.contactNo && user.contactNo.toLowerCase().includes(query)) ||
      (user.extNo && user.extNo.toLowerCase().includes(query))
    );
  });

  // Pagination logic
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  return (
    <div className="user-management-container">
      <div className="management-header">
        <h2>User Management</h2>
        <div style={{ display: 'flex', gap: '10px' }}>
          <label 
            htmlFor="bulk-upload-input" 
            className="btn btn-secondary"
            style={{ 
              cursor: uploading ? 'not-allowed' : 'pointer',
              opacity: uploading ? 0.6 : 1,
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <i className="fas fa-upload"></i>
            {uploading ? 'Uploading...' : 'Bulk Add Users'}
          </label>
          <input
            id="bulk-upload-input"
            type="file"
            accept=".xlsx,.xls"
            onChange={handleBulkUpload}
            disabled={uploading}
            style={{ display: 'none' }}
          />
          <button className="btn btn-primary" onClick={handleAddClick}>
            <i className="fas fa-plus"></i> Add User
          </button>
        </div>
      </div>

      {saveMessage && (
        <div className="alert alert-success" role="alert">
          {saveMessage}
        </div>
      )}

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {/* Search Bar */}
      <div style={{ marginTop: '20px', marginBottom: '15px' }}>
        <div style={{ position: 'relative', maxWidth: '400px' }}>
          <input
            type="text"
            className="form-control"
            placeholder="Search by name, email, phone, or extension..."
            value={searchQuery}
            onChange={handleSearchChange}
            style={{
              paddingLeft: '35px',
              borderRadius: '4px',
              border: '1px solid #ddd'
            }}
          />
          <i 
            className="fas fa-search" 
            style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#6c757d'
            }}
          ></i>
        </div>
        {searchQuery && (
          <div style={{ marginTop: '8px', fontSize: '14px', color: '#6c757d' }}>
            Found {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      ) : (
        <div className="applications-table" style={{ marginTop: '20px', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', tableLayout: 'fixed' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ddd', fontWeight: 'bold', width: '12%' }}>Name</th>
                <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ddd', fontWeight: 'bold', width: '15%' }}>Email</th>
                <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ddd', fontWeight: 'bold', width: '12%' }}>Designation</th>
                <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ddd', fontWeight: 'bold', width: '12%' }}>Department</th>
                <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ddd', fontWeight: 'bold', width: '6%' }}>Ext</th>
                <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ddd', fontWeight: 'bold', width: '10%' }}>Contact</th>
                <th style={{ padding: '8px', textAlign: 'center', border: '1px solid #ddd', fontWeight: 'bold', width: '10%' }}>Role</th>
                <th style={{ padding: '8px', textAlign: 'center', border: '1px solid #ddd', fontWeight: 'bold', width: '8%' }}>Status</th>
                <th style={{ padding: '8px', textAlign: 'center', border: '1px solid #ddd', fontWeight: 'bold', width: '10%' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentUsers.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ padding: '20px', textAlign: 'center', color: '#6c757d' }}>
                    {searchQuery ? 'No users found matching your search' : 'No users available'}
                  </td>
                </tr>
              ) : (
                currentUsers.map((user) => (
                <tr key={user.id} style={{ borderBottom: '1px solid #ddd' }}>
                  <td style={{ padding: '8px', border: '1px solid #ddd', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}><strong>{user.employeeName}</strong></td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.email}</td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.designation || '-'}</td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.department || '-'}</td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>{user.extNo || '-'}</td>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>{user.contactNo || '-'}</td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                    <span style={{
                      padding: '3px 8px',
                      backgroundColor: user.roleId === 4 ? '#dc3545' : user.roleId === 2 || user.roleId === 3 ? '#ffc107' : '#17a2b8',
                      color: user.roleId === 2 || user.roleId === 3 ? '#000' : 'white',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: '600',
                      whiteSpace: 'nowrap'
                    }}>
                      {user.roleName || 'Employee'}
                    </span>
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                    <button
                      onClick={() => handleToggleStatus(user.id)}
                      style={{
                        padding: '3px 8px',
                        backgroundColor: user.isActive ? '#28a745' : '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '11px',
                        fontWeight: '600',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {user.isActive ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                      <button
                        onClick={() => handleEditClick(user)}
                        title="Edit User"
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '16px',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#e3f2fd'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDeleteClick(user)}
                        title="Delete User"
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '16px',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#ffebee'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              )))}
            </tbody>
          </table>

          {/* Pagination */}
          {filteredUsers.length > usersPerPage && (
            <div style={{ 
              marginTop: '24px', 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              padding: '16px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #e9ecef'
            }}>
              <div style={{ fontSize: '14px', color: '#495057', fontWeight: '500' }}>
                Showing <strong>{indexOfFirstUser + 1}</strong> to <strong>{Math.min(indexOfLastUser, filteredUsers.length)}</strong> of <strong>{filteredUsers.length}</strong> users
              </div>
              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #dee2e6',
                    backgroundColor: currentPage === 1 ? '#e9ecef' : 'white',
                    color: currentPage === 1 ? '#6c757d' : '#495057',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s',
                    boxShadow: currentPage === 1 ? 'none' : '0 1px 2px rgba(0,0,0,0.05)'
                  }}
                  onMouseEnter={(e) => {
                    if (currentPage !== 1) {
                      e.currentTarget.style.backgroundColor = '#f8f9fa';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (currentPage !== 1) {
                      e.currentTarget.style.backgroundColor = 'white';
                    }
                  }}
                >
                  ← Previous
                </button>
                
                <div style={{ display: 'flex', gap: '4px' }}>
                  {(() => {
                    const pages = [];
                    const maxVisible = 5;
                    let startPage = Math.max(1, currentPage - 2);
                    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
                    
                    if (endPage - startPage < maxVisible - 1) {
                      startPage = Math.max(1, endPage - maxVisible + 1);
                    }
                    
                    if (startPage > 1) {
                      pages.push(
                        <button
                          key={1}
                          onClick={() => handlePageChange(1)}
                          style={{
                            padding: '8px 12px',
                            border: '1px solid #dee2e6',
                            backgroundColor: 'white',
                            color: '#495057',
                            cursor: 'pointer',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontWeight: '500',
                            minWidth: '40px',
                            transition: 'all 0.2s',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                          }}
                        >
                          1
                        </button>
                      );
                      if (startPage > 2) {
                        pages.push(<span key="dots1" style={{ padding: '8px 4px', color: '#6c757d' }}>...</span>);
                      }
                    }
                    
                    for (let i = startPage; i <= endPage; i++) {
                      pages.push(
                        <button
                          key={i}
                          onClick={() => handlePageChange(i)}
                          style={{
                            padding: '8px 12px',
                            border: currentPage === i ? '1px solid #007bff' : '1px solid #dee2e6',
                            backgroundColor: currentPage === i ? '#007bff' : 'white',
                            color: currentPage === i ? 'white' : '#495057',
                            cursor: 'pointer',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontWeight: currentPage === i ? '600' : '500',
                            minWidth: '40px',
                            transition: 'all 0.2s',
                            boxShadow: currentPage === i ? '0 2px 4px rgba(0,123,255,0.3)' : '0 1px 2px rgba(0,0,0,0.05)'
                          }}
                          onMouseEnter={(e) => {
                            if (currentPage !== i) {
                              e.currentTarget.style.backgroundColor = '#f8f9fa';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (currentPage !== i) {
                              e.currentTarget.style.backgroundColor = 'white';
                            }
                          }}
                        >
                          {i}
                        </button>
                      );
                    }
                    
                    if (endPage < totalPages) {
                      if (endPage < totalPages - 1) {
                        pages.push(<span key="dots2" style={{ padding: '8px 4px', color: '#6c757d' }}>...</span>);
                      }
                      pages.push(
                        <button
                          key={totalPages}
                          onClick={() => handlePageChange(totalPages)}
                          style={{
                            padding: '8px 12px',
                            border: '1px solid #dee2e6',
                            backgroundColor: 'white',
                            color: '#495057',
                            cursor: 'pointer',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontWeight: '500',
                            minWidth: '40px',
                            transition: 'all 0.2s',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                          }}
                        >
                          {totalPages}
                        </button>
                      );
                    }
                    
                    return pages;
                  })()}
                </div>
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #dee2e6',
                    backgroundColor: currentPage === totalPages ? '#e9ecef' : 'white',
                    color: currentPage === totalPages ? '#6c757d' : '#495057',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s',
                    boxShadow: currentPage === totalPages ? 'none' : '0 1px 2px rgba(0,0,0,0.05)'
                  }}
                  onMouseEnter={(e) => {
                    if (currentPage !== totalPages) {
                      e.currentTarget.style.backgroundColor = '#f8f9fa';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (currentPage !== totalPages) {
                      e.currentTarget.style.backgroundColor = 'white';
                    }
                  }}
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Add/Edit User Modal */}
      {isModalOpen && (
        <div className="modal show d-block" tabIndex={-1} role="dialog">
          <div className="modal-dialog modal-lg" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingUser?.id ? 'Edit User' : 'Add New User'}
                </h5>
                <button
                  type="button"
                  className="close"
                  onClick={() => {
                    setIsModalOpen(false);
                    setEditingUser(null);
                  }}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <form>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>Employee Name *</label>
                        <input
                          type="text"
                          className="form-control"
                          value={editingUser?.employeeName || ''}
                          onChange={(e) =>
                            setEditingUser({ ...editingUser, employeeName: e.target.value })
                          }
                          required
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>Email *</label>
                        <input
                          type="email"
                          className="form-control"
                          value={editingUser?.email || ''}
                          onChange={(e) =>
                            setEditingUser({ ...editingUser, email: e.target.value })
                          }
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>Designation</label>
                        <input
                          type="text"
                          className="form-control"
                          value={editingUser?.designation || ''}
                          onChange={(e) =>
                            setEditingUser({ ...editingUser, designation: e.target.value })
                          }
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>Department</label>
                        <input
                          type="text"
                          className="form-control"
                          value={editingUser?.department || ''}
                          onChange={(e) =>
                            setEditingUser({ ...editingUser, department: e.target.value })
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>Extension Number</label>
                        <input
                          type="text"
                          className="form-control"
                          value={editingUser?.extNo || ''}
                          onChange={(e) =>
                            setEditingUser({ ...editingUser, extNo: e.target.value })
                          }
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>Contact Number</label>
                        <input
                          type="text"
                          className="form-control"
                          value={editingUser?.contactNo || ''}
                          onChange={(e) =>
                            setEditingUser({ ...editingUser, contactNo: e.target.value })
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>Building</label>
                        <input
                          type="text"
                          className="form-control"
                          value={editingUser?.building || ''}
                          onChange={(e) =>
                            setEditingUser({ ...editingUser, building: e.target.value })
                          }
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>Floor</label>
                        <input
                          type="text"
                          className="form-control"
                          value={editingUser?.floor || ''}
                          onChange={(e) =>
                            setEditingUser({ ...editingUser, floor: e.target.value })
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>Employee Code</label>
                        <input
                          type="text"
                          className="form-control"
                          value={editingUser?.employeeCode || ''}
                          onChange={(e) =>
                            setEditingUser({ ...editingUser, employeeCode: e.target.value })
                          }
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>Location</label>
                        <input
                          type="text"
                          className="form-control"
                          value={editingUser?.location || ''}
                          onChange={(e) =>
                            setEditingUser({ ...editingUser, location: e.target.value })
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-4">
                      <div className="form-group">
                        <label>Date of Joining</label>
                        <input
                          type="date"
                          className="form-control"
                          value={editingUser?.dateOfJoining || ''}
                          onChange={(e) =>
                            setEditingUser({ ...editingUser, dateOfJoining: e.target.value })
                          }
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group">
                        <label>Date of Birth</label>
                        <input
                          type="date"
                          className="form-control"
                          value={editingUser?.dateOfBirth || ''}
                          onChange={(e) =>
                            setEditingUser({ ...editingUser, dateOfBirth: e.target.value })
                          }
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group">
                        <label>Wedding Anniversary</label>
                        <input
                          type="date"
                          className="form-control"
                          value={editingUser?.weddingAnniversary || ''}
                          onChange={(e) =>
                            setEditingUser({ ...editingUser, weddingAnniversary: e.target.value })
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Role *</label>
                    <select
                      className="form-control"
                      value={editingUser?.roleId || 1}
                      onChange={(e) =>
                        setEditingUser({ ...editingUser, roleId: parseInt(e.target.value) })
                      }
                      required
                    >
                      {roles.map((role) => (
                        <option key={role.id} value={role.id}>
                          {role.name} - {role.description}
                        </option>
                      ))}
                    </select>
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setIsModalOpen(false);
                    setEditingUser(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={!editingUser?.employeeName || !editingUser?.email}
                >
                  {editingUser?.id ? 'Update User' : 'Add User'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && userToDelete && (
        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setUserToDelete(null);
          }}
          onConfirm={handleDelete}
          itemName={userToDelete.employeeName}
        />
      )}

      {/* Modal Backdrop */}
      {(isModalOpen || showDeleteModal) && <div className="modal-backdrop show"></div>}
    </div>
  );
};

export default UserManagement;
